import { useQuery } from "@tanstack/react-query";
import { getFigureDrawing } from "@/services/figures";

// A render takes well under this, and the api answers a repeat ask from the
// store rather than sending the worker again.
const ASK_AGAIN_AFTER_MS = 2_000;

/** A figure's drawing as a card shows it: its address once drawn, or why it could not be asked for. */
export interface FigureDrawing {
  /** The signed address, once the drawing exists. */
  url: string | undefined;
  /** Why the last ask failed, or null while none has. */
  error: unknown;
  /** Ask again. */
  retry: () => void;
}

/**
 * One figure's formal drawing, cached under the key every review surface shares so the
 * thumbnail and the sheet cost one request between them. The first ask sends the worker to
 * draw; until the drawing exists the api answers pending with no address, so the url stays
 * undefined and the hook asks again, in a hidden tab too, since it asks only while the
 * drawing is pending. An ask that fails stops the asking, so the failure is returned for the
 * card to say, with the way to ask again.
 */
export function useFigureDrawing(
  taskId: string,
  fileId: string,
): FigureDrawing {
  const { data, error, refetch } = useQuery({
    queryKey: ["figure-drawing", taskId, fileId],
    queryFn: () => getFigureDrawing(taskId, fileId),
    staleTime: 60_000,
    refetchInterval: (query) =>
      query.state.data?.status === "pending" ? ASK_AGAIN_AFTER_MS : false,
    refetchIntervalInBackground: true,
  });
  return {
    url: data?.status === "ready" ? (data.drawing_url ?? undefined) : undefined,
    error,
    retry: () => void refetch(),
  };
}
